/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

import { formatCurrency } from '@ahm/common-helpers';
import { Button, SimpleGrid, Skeleton, Stack, Text, rem } from '@mantine/core';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useGetTips } from '../../../features/booking/api/master-data/use-get-tips';
import useGetServiceDetail from '@/api/services/use-get-service-detail';
import AhaNumberInput from '@/components/common/AhaNumberInput';
import { useRemoteConfigHook } from '@/hooks';
import { TIP_TYPE } from '@/lib/constants';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

interface TipSectionProps {
  mode?: 'rating' | 'record';
}

export function TipSection({ mode = 'rating' }: TipSectionProps) {
  const {
    control,
    watch,
    setValue,
    clearErrors,
    trigger,
    formState: { errors },
  } = useFormContext<any>();
  const selectedTipOption = watch('selectedTipOption');
  const tipInput = watch('tipInput');

  const { orderDetails: order } = useBookingStore((state) => state);
  const { tipsDeadlineConfig } = useRemoteConfigHook();

  const { data: tipOptions, isLoading: isLoadingTips } = useGetTips(
    {
      type: TIP_TYPE.FLAT,
    },
    { enabled: !!order?.service?._id }
  );

  const { data: serviceDetails } = useGetServiceDetail(
    { service_id: order?.service?._id || '', format: 'json' },
    {
      enabled: !!order?.service?._id,
      queryKey: [order?.service?._id, 'service-detail'],
    }
  );

  const handleTipOptionSelect = (tipValue: number) => {
    if (tipInput) {
      setValue('tipInput', undefined);
      void trigger('tipInput');
    }

    if (selectedTipOption === tipValue) {
      setValue('selectedTipOption', null);
    } else {
      setValue('selectedTipOption', tipValue);
    }
  };

  const handleTipInputChange = (value: string | number | null) => {
    const numValue = Number(value);
    setValue('tipInput', numValue || undefined);

    if (numValue !== selectedTipOption) {
      setValue('selectedTipOption', null);
    }
  };

  const isShowTipSection = useMemo(() => {
    // Special case: orders in process but not completed (like record delivery)
    if (
      !order?.complete_time &&
      serviceDetails?.is_tip_allowed &&
      order?.supplier_id
    ) {
      return true;
    }

    // Standard case: must be completed & tip allowed
    if (!order?.complete_time || !serviceDetails?.is_tip_allowed) return false;

    if (!tipsDeadlineConfig) return false;

    const deadlineSeconds = Number(tipsDeadlineConfig);
    if (deadlineSeconds <= 0) return true;

    const completeTimeMs = (order as any).complete_time * 1000;
    const diffInSeconds = dayjs().diff(dayjs(completeTimeMs), 'second');

    return diffInSeconds <= deadlineSeconds;
  }, [order, serviceDetails, tipsDeadlineConfig]);

  if (!isShowTipSection) return null;

  return (
    <Stack gap={rem(16)} p={mode === 'rating' ? rem(16) : 0} w="100%">
      {mode === 'rating' ? (
        <Text fz={rem(16)} fw={600} c="neutral.7">
          Gửi tip để cảm ơn tài xế nhé! Tài xế sẽ nhận được 100% số tiền này.
        </Text>
      ) : null}

      <SimpleGrid cols={3} spacing={rem(12)}>
        {isLoadingTips
          ? Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} height={rem(48)} width="100%" radius="md" />
            ))
          : tipOptions?.map((tip) => (
              <Button
                key={tip.name}
                variant="outline"
                bg={selectedTipOption === tip.value ? 'primary.1' : 'white'}
                c={selectedTipOption === tip.value ? 'primary.5' : 'neutral.7'}
                radius="md"
                size="md"
                w="100%"
                styles={(theme) => ({
                  root: {
                    borderColor:
                      selectedTipOption === tip.value
                        ? theme.colors.primary[7]
                        : theme.colors.neutral[2],
                  },
                })}
                onClick={() => handleTipOptionSelect(tip.value)}
              >
                {formatCurrency(tip.value)}
              </Button>
            ))}
      </SimpleGrid>

      {isLoadingTips ? (
        <Skeleton height={rem(56)} radius="md" />
      ) : (
        <Controller
          name="tipInput"
          control={control}
          render={({ field }) => (
            <>
              <AhaNumberInput
                value={field.value ?? undefined}
                onChange={(value) => {
                  field.onChange(value);
                  handleTipInputChange(value);
                }}
                onFocus={() => setValue('selectedTipOption', null)}
                label="Nhập số tiền bạn muốn tip"
                min={0}
                thousandSeparator="."
                decimalSeparator=","
                error={errors.tipInput?.message as string}
                className="w-full"
                clearable
                onClear={() => {
                  clearErrors('tipInput');
                  handleTipInputChange(null);
                }}
              />
            </>
          )}
        />
      )}
    </Stack>
  );
}
